import { MouseEventHandler, ReactNode, useId } from 'react';
import { Avatar, AvatarProps, chakra } from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import Text, { TextProps } from '../Text/Text';
import Divider from '../Divider/Divider';
import Box from '../Box/Box';
import Icon from '../Icon/Icon';
import Checkbox from '../Form/Checkbox/Checkbox';
import { useDropdownContext, useDropdownStyles } from './Dropdown.context';

export type DropdownOptionProps<T> = {
  avatar?: AvatarProps;
  value?: T | null;
  children?: ReactNode;
  'aria-label'?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  isDisabled?: boolean;
};

const DropdownOption = <T = string,>({
  avatar,
  children,
  isDisabled,
  onClick,
  value = null,
  ...rest
}: DropdownOptionProps<T>) => {
  const { item } = useDropdownStyles();
  const ctx = useDropdownContext<T | null>();
  const { index } = rest as { index?: number };
  const isSelected = !!(
    ctx.formValue && (Array.isArray(ctx.formValue) ? ctx.formValue.includes(value) : ctx.formValue === value)
  );

  return (
    <chakra.div
      ref={(node) => {
        if (ctx.listRef.current) {
          ctx.listRef.current[index!] = node;
        }
      }}
      aria-disabled={isDisabled || undefined}
      id={useId()}
      role="option"
      {...rest}
      __css={item}
      className={cx(
        isSelected && 'bitkit-select__option-active',
        index === ctx.activeIndex && 'bitkit-select__option-hover',
      )}
      {...ctx.getItemProps(
        isDisabled
          ? {}
          : {
              onClick(event) {
                onClick?.(event);

                if (!event.defaultPrevented) {
                  ctx.onOptionSelected({ index, label: children, value });
                }
              },
            },
      )}
    >
      <Box display="flex" alignItems="center" gap="8">
        {avatar && <Avatar size={ctx.size === 'lg' ? '32' : '24'} {...avatar} />}
        {ctx.isMultiSelect ? (
          <>
            <Checkbox isChecked={isSelected} />
            <Box flex="1">{children}</Box>
          </>
        ) : (
          <>
            <Box flex="1">{children}</Box>
            {isSelected && <Icon name="Check" color="icon/interactive" />}
          </>
        )}
      </Box>
    </chakra.div>
  );
};

const DropdownGroup = ({
  children,
  label,
  labelProps = {},
}: {
  children: ReactNode;
  label: string;
  labelProps?: TextProps;
}) => {
  const { group } = useDropdownStyles();
  const textId = useId();
  return (
    <div aria-labelledby={textId} role="group">
      <chakra.div __css={group}>
        <Text color="neutral.60" fontWeight="bold" id={textId} size="1" {...labelProps}>
          {label}
        </Text>
        <Divider color="neutral.93" />
      </chakra.div>
      {children}
    </div>
  );
};

type DropdownDetailedOptionProps<T> = DropdownOptionProps<T> & {
  icon: ReactNode;
  title: string;
  subtitle: string;
};

const DropdownDetailedOption = <T = string,>({
  icon,
  title,
  subtitle,
  isDisabled,
  ...rest
}: DropdownDetailedOptionProps<T>) => {
  return (
    <DropdownOption isDisabled={isDisabled} {...rest} aria-label={title}>
      <chakra.div alignItems="center" display="flex" flexDir="row" gap="12">
        <chakra.div opacity={isDisabled ? '0.5' : '1'}>{icon}</chakra.div>
        <chakra.div>
          {title}
          <Text color={isDisabled ? 'text/disabled' : 'input/text/helper'} size="2">
            {subtitle}
          </Text>
        </chakra.div>
      </chakra.div>
    </DropdownOption>
  );
};
DropdownDetailedOption.searchable = true;

export { DropdownOption, DropdownGroup, DropdownDetailedOption };
